import type { ReactNode } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'
import { homeForRoles, useSession } from '@/lib/mobility-auth'
import type { Role } from '@/lib/mobility'

/**
 * Pathless ROLE guard — the SPA-side gate wrapping each portal subtree so a
 * principal only sees the surface their roles (API_SPEC §13) allow. Two failure
 * paths:
 *   • NOT signed in → redirect to `/login?from=<attempted>` so login returns there.
 *   • Signed in, WRONG role → redirect to the principal's OWN home (never a dead end).
 *
 * This is UX, not the security boundary: the REAL enforcement is server-side
 * (tmext_mobility repositories are entity-scoped + role-gated, M-2/M-9). The
 * guard just avoids flashing the wrong shell and routes cleanly. EMPLOYEE is
 * implicit for every active roster, so `allow={['EMPLOYEE']}` admits anyone
 * signed in.
 */
export interface RequireRoleProps {
  /** Roles permitted on this subtree. A principal holding ANY of them passes. */
  allow: readonly Role[]
  /** Optional explicit children; defaults to the nested <Outlet /> (layout-route usage). */
  children?: ReactNode
}

export function RequireRole({ allow, children }: RequireRoleProps) {
  const { loading, signedIn, roles } = useSession()
  const location = useLocation()

  if (loading) return <GuardFallback />

  if (!signedIn) {
    const from = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?from=${from}`} replace />
  }

  if (!allow.some((r) => roles.includes(r))) {
    return <Navigate to={homeForRoles(roles)} replace />
  }

  return <>{children ?? <Outlet />}</>
}

/**
 * Inverse guard for the public login page: a signed-in principal is sent to
 * their home (honouring a safe `?from=` return path) instead of re-seeing login.
 */
export function RequireAnon({ children }: { children?: ReactNode }) {
  const { loading, signedIn, roles } = useSession()
  const location = useLocation()

  if (loading) return <GuardFallback />

  if (signedIn) {
    const dest = safeReturnPath(new URLSearchParams(location.search).get('from')) ?? homeForRoles(roles)
    return <Navigate to={dest} replace />
  }

  return <>{children ?? <Outlet />}</>
}

/** Only same-origin in-app return paths (never a protocol-relative open redirect). */
function safeReturnPath(from: string | null): string | null {
  if (!from) return null
  const decoded = decodeURIComponent(from)
  return decoded.startsWith('/') && !decoded.startsWith('//') ? decoded : null
}

/** Accessible, content-free placeholder shown WHILE the session resolves. */
function GuardFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      className="min-h-screen flex items-center justify-center text-muted-foreground text-sm"
    >
      Checking your session…
    </div>
  )
}
